/**
 * Recordings Panel Component
 *
 * Compact recordings list for the right panel
 */

import React, { useEffect, useState } from 'react';
import { Video, Play, Download, Trash2, X, Loader2 } from 'lucide-react';
import { useRecordings } from '@/hooks/useRecordings';
import { useAuth } from '@/hooks/useAuth';
import type { SessionRecording } from '@/types/api.types';

interface RecordingsPanelProps {
  onClose: () => void;
}

export const RecordingsPanel: React.FC<RecordingsPanelProps> = ({ onClose }) => {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  const {
    recordings,
    isLoading,
    error,
    downloadUrl,
    isLoadingUrl,
    fetchRecordings,
    fetchDownloadUrl,
    deleteRecording,
  } = useRecordings();

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  const handlePlay = async (recording: SessionRecording) => {
    // Fetch download URL and open in new tab
    try {
      await fetchDownloadUrl(recording.id);
      // The downloadUrl state will be updated after fetch
    } catch (error) {
      console.error('Failed to get recording URL:', error);
    }
  };

  // Effect to open URL when it's fetched
  useEffect(() => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  }, [downloadUrl]);

  const handleDownload = async (recording: SessionRecording) => {
    setDownloadingId(recording.id);
    try {
      await fetchDownloadUrl(recording.id);
      // Download will trigger via useEffect when downloadUrl updates
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Сигурни ли сте, че искате да изтриете този запис?')) {
      try {
        await deleteRecording(id);
      } catch (error) {
        console.error('Failed to delete recording:', error);
      }
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('bg-BG', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border-primary flex items-center justify-between bg-white dark:bg-dark-bg-secondary">
        <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary flex items-center gap-2">
          <Video className="w-5 h-5" />
          Записи
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary transition-colors p-1"
          title="Затвори"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        ) : recordings.length === 0 ? (
          <div className="p-8 text-center">
            <Video className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
              Няма записи
            </p>
            <p className="text-xs text-gray-400 dark:text-dark-text-muted mt-1">
              Записите от гласови канали ще се появят тук
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-dark-border-primary">
            {recordings.map((recording) => (
              <div
                key={recording.id}
                className="p-3 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors"
              >
                <div className="flex items-start gap-3">
                  {/* Thumbnail / Icon */}
                  <div className="w-10 h-10 rounded bg-gray-200 dark:bg-dark-bg-input flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">
                      {recording.title || recording.channelName || 'Запис'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-muted">
                      {formatDate(recording.startedAt)}
                      {recording.durationSeconds && ` • ${formatDuration(recording.durationSeconds)}`}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePlay(recording)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                      title="Пусни"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDownload(recording)}
                      disabled={downloadingId === recording.id || isLoadingUrl}
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors disabled:opacity-50"
                      title="Изтегли"
                    >
                      {downloadingId === recording.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(recording.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Изтрий"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordingsPanel;
